import React, { useState } from "react";
import { Download, Share2, MessageCircle, Mail, Loader2, X, Copy, Check, Printer } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

async function downloadPdf(topupId) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}/topups/${topupId}/receipt.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `receipt-${topupId.slice(0, 8).toUpperCase()}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReceiptActions({ topupId }) {
  const [busy, setBusy] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const { brand } = useBranding();
  const brandName = brand.brand_name || "My GlobalTopup";

  const getShareInfo = async () => {
    if (shareInfo) return shareInfo;
    const { data } = await api.get(`/topups/${topupId}/receipt/share`);
    setShareInfo(data);
    return data;
  };

  const onDownload = async () => {
    setBusy(true);
    try {
      await downloadPdf(topupId);
      toast.success("Receipt downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const onShare = async () => {
    setBusy(true);
    try {
      const info = await getShareInfo();
      const url = `${API.replace(/\/api$/, "")}${info.public_path}`;
      if (navigator.share) {
        await navigator.share({
          title: `${brandName} Receipt #${info.receipt_no}`,
          text: `Topup receipt #${info.receipt_no}`,
          url,
        });
      } else {
        setShowShare(true);
      }
    } catch (e) {
      if (e.name !== "AbortError") setShowShare(true);
    } finally {
      setBusy(false);
    }
  };

  const onWhatsApp = async () => {
    setBusy(true);
    try {
      const info = await getShareInfo();
      const url = `${API.replace(/\/api$/, "")}${info.public_path}`;
      const text = encodeURIComponent(
        `Your ${brandName} receipt #${info.receipt_no}\n${url}`
      );
      window.open(`https://wa.me/?text=${text}`, "_blank");
    } catch (e) {
      toast.error("Could not generate share link");
    } finally {
      setBusy(false);
    }
  };

  const onPrint = async () => {
    setBusy(true);
    try {
      const info = await getShareInfo();
      const url = `${API.replace(/\/api$/, "")}${info.public_path}?print=1`;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) toast.error("Pop-up blocked — allow pop-ups to print");
    } catch (e) {
      toast.error("Could not open print view");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <ActionButton onClick={onDownload} icon={Download} label="Download PDF" testid="receipt-download-btn" busy={busy} />
        <ActionButton onClick={onShare} icon={Share2} label="Share PDF" testid="receipt-share-btn" busy={busy} />
        <ActionButton onClick={onWhatsApp} icon={MessageCircle} label="WhatsApp" testid="receipt-whatsapp-btn" busy={busy} />
        <ActionButton onClick={() => setShowEmail(true)} icon={Mail} label="Email" testid="receipt-email-btn" busy={busy} />
        <ActionButton onClick={onPrint} icon={Printer} label="Print Receipt" testid="receipt-print-btn" busy={busy} />
      </div>

      {showShare && shareInfo && (
        <ShareModal info={shareInfo} onClose={() => setShowShare(false)} />
      )}
      {showEmail && (
        <EmailModal topupId={topupId} onClose={() => setShowEmail(false)} />
      )}
    </>
  );
}

function ActionButton({ onClick, icon: Icon, label, testid, busy }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-[#0F62FE] hover:text-[#0F62FE] transition-colors disabled:opacity-50"
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function ShareModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = `${API.replace(/\/api$/, "")}${info.public_path}`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Share receipt link
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Anyone with this link can view receipt #{info.receipt_no}.
        </p>
        <div className="flex items-center gap-2">
          <input
            data-testid="receipt-share-link"
            readOnly
            value={url}
            className="flex-1 h-10 px-3 rounded-lg border border-slate-300 bg-slate-50 text-xs font-mono text-slate-700 focus:outline-none"
          />
          <button
            onClick={copy}
            className="inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6]"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 block text-center text-xs font-semibold text-[#0F62FE] hover:underline"
        >
          Open public receipt →
        </a>
      </div>
    </div>
  );
}

function EmailModal({ topupId, onClose }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/topups/${topupId}/receipt/email`, {
        email: email.trim(),
        message: msg || null,
      });
      if (data.ok) {
        toast.success(`Email sent to ${email}`);
        onClose();
      } else {
        toast.error(data.error || "Email failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={busy ? undefined : onClose} />
      <form
        onSubmit={submit}
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Email receipt to customer
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
          Customer email
        </label>
        <input
          data-testid="receipt-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="customer@example.com"
          className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
        />
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3 mb-1.5">
          Message (optional)
        </label>
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent resize-none"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="receipt-email-submit"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Send PDF
          </button>
        </div>
      </form>
    </div>
  );
}
